// src/utils/cmsAuth.js
const API_BASE =
  process.env.REACT_APP_FASTAPI_ADS_URL ||
  process.env.REACT_APP_FASTAPI_SEVER_URL ||
  process.env.NEXT_PUBLIC_API_BASE ||
  "";

const TOKEN_KEY = "cms_access_token";
const REFRESH_TOKEN_KEY = "cms_refresh_token";
let refreshPromise = null;

function authError(code, status) {
  const error = new Error(code);
  error.code = code;
  error.status = status;
  return error;
}

export function isSessionExpired(error) {
  return ["NO_TOKEN", "NO_REFRESH_TOKEN", "REFRESH_REJECTED", "UNAUTHORIZED"]
    .includes(error?.code);
}

export function setToken(token) {
  try { localStorage.setItem(TOKEN_KEY, token); } catch {}
}
export function setTokens(accessToken, refreshToken) {
  try {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch {}
}
export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}
export function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_TOKEN_KEY) || ""; } catch { return ""; }
}
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {}
}

export async function cmsLogin(username, password) {
  const res = await fetch(`${API_BASE}/admin/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const msg = (await res.json().catch(()=>({})))?.detail || "로그인 실패";
    throw new Error(msg);
  }
  return res.json();
}

export async function cmsMe() {
  if (!getToken() && !getRefreshToken()) throw authError("NO_TOKEN");
  const res = await authFetch("/admin/me");
  if (!res.ok) {
    throw authError(res.status === 401 ? "UNAUTHORIZED" : "AUTH_CHECK_FAILED", res.status);
  }
  return res.json();
}

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw authError("NO_REFRESH_TOKEN");
    const res = await fetch(`${API_BASE}/admin/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) {
      const latestRefreshToken = getRefreshToken();
      if (latestRefreshToken && latestRefreshToken !== refreshToken && getToken()) {
        return getToken();
      }
      throw authError(res.status === 401 ? "REFRESH_REJECTED" : "REFRESH_FAILED", res.status);
    }
    const data = await res.json();
    if (!data.access_token || !data.refresh_token) throw authError("INVALID_REFRESH_RESPONSE");
    // Do not restore a session that was logged out or replaced during refresh.
    if (getRefreshToken() !== refreshToken) {
      if (getToken() && getRefreshToken()) return getToken();
      throw authError("NO_TOKEN");
    }
    setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export function expireSession() {
  clearToken();
  window.dispatchEvent(new Event("cms-auth-expired"));
}

function request(url, opts, token) {
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_BASE}${url}`, { ...opts, headers });
}

export async function authFetch(url, opts = {}) {
  let token = getToken();
  if (!token && getRefreshToken()) {
    try {
      token = await refreshAccessToken();
    } catch (error) {
      if (isSessionExpired(error)) expireSession();
      throw error;
    }
  }
  let res = await request(url, opts, token);
  if (res.status === 401 && getRefreshToken()) {
    try {
      token = await refreshAccessToken();
    } catch (error) {
      if (!isSessionExpired(error)) throw error;
      expireSession();
      return res;
    }
    res = await request(url, opts, token);
  }
  if (res.status === 401) expireSession();
  return res;
}

// 기존 파일에 추가
export async function changePassword(oldPw, newPw) {
  const res = await authFetch("/admin/change/password", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ old_password: oldPw, new_password: newPw }),
  });
  if (!res.ok) {
    const msg = (await res.json().catch(()=>({})))?.detail || "비밀번호 변경 실패";
    throw new Error(msg);
  }
  return res.json(); // { ok: true }
}
